import React, { useState, useContext, useEffect, useId, useRef } from 'react';
import {
	Box,
	Button,
	CircularProgress,
	Grid,
	IconButton,
	Stack,
	Typography
} from '@mui/material';
import { isEmpty } from 'lodash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPenToSquare, faCamera } from '@fortawesome/free-solid-svg-icons';
import ZoomInIcon from '@mui/icons-material/ZoomIn';

import { getToken } from '../../../api/Account';
import { DataContext } from '../../../context/DataContext';
import {
	getCommunitiesByMunAsync,
	getMunicipalitiesByDeptopAsync,
	getOnlyDepartmentListAsync
} from '../../../api/Locations';
import { simpleMessage } from '../../../helpers/Helpers';
import { tienePermiso } from '../../../helpers/permisos';

import { getClientByIdAsync, updateClientAsync } from '../../../api/Clients';
import ClientPhoto from './photoComponent/ClientPhoto';
import PreviewModal from '../../../components/modals/PreviewModal';
import { EstadoError, SeccionFormulario, useAviso } from '../../../components/base';
import {
	CargandoFormularioCliente,
	ERROR_OTRA_SESION,
	FormularioCliente,
	llevarAlPrimerError,
	nombreCompleto,
	validarCliente
} from '../comunes';

const VALORES_INICIALES = {
	firstName: '',
	secondName: '',
	lastName: '',
	secondLastName: '',
	identification: '',
	phone: '',
	sex: '',
	civilStatus: '',
	department: '',
	municipality: '',
	community: '',
	address: '',
	actividadEconomica: '',
	company: '',
	companyAddress: ''
};

const getCivilStatus = (data) => {
	if (data === 'SOLTERO' || data === 'SOLTERA') {
		return 0;
	} else if (data === 'CASADO' || data === 'CASADA') {
		return 1;
	} else {
		return 2;
	}
};

const nombreDe = (lista, id) => lista.find((item) => item.id === id)?.name;

const ClientDetails = ({ id, onDatos, onFallo }) => {
	const token = getToken();
	const prefijo = useId();

	const [departmentList, setDepartmentList] = useState([]);
	const [MunicipalitiesList, setMunicipalitiesList] = useState([]);
	const [communitiesList, setCommunitiesList] = useState([]);
	const [cargandoUbicacion, setCargandoUbicacion] = useState({});

	const [valores, setValores] = useState(VALORES_INICIALES);
	// Errores de validación junto a cada campo: { campo: 'texto' }.
	const [errores, setErrores] = useState({});

	const [isEdit, setIsEdit] = useState(false);

	const [clientPhotos, setClientPhotos] = useState([]);
	const [editPhotos, setEditPhotos] = useState(false);
	const [localReload, setLocalReload] = useState(false);

	const [openModal, setOpenModal] = useState(false);
	const [selectedImage, setSelectedImage] = useState('');

	// Estado local de la pestaña: esqueleto mientras carga y error con "Reintentar".
	const [cargando, setCargando] = useState(true);
	const [error, setError] = useState(false);
	const [nombre, setNombre] = useState('');

	// El overlay de carga no bloquea el teclado: el ref corta un segundo envío al instante
	// y el estado deshabilita el botón (misma protección que AddCredit y AddAbono).
	const enviando = useRef(false);
	const [guardando, setGuardando] = useState(false);
	const formulario = useRef(null);
	// Respuestas de la cascada que llegan tarde (se eligió otra opción mientras tanto) se descartan.
	const peticionMunicipios = useRef(0);
	const peticionComunidades = useRef(0);

	const { setIsLoading, setIsUnautorized, access } = useContext(DataContext);
	const aviso = useAviso();
	// Cada acción pide el permiso que exige su endpoint (ClientsController): guardar es UpdateClient
	// (CLIENTS UPDATE) y cambiar las fotos es UpdateClientPhotos (CLIENTS CREATE).
	const puedeEditar = tienePermiso(access, 'CLIENTS UPDATE');
	const puedeCambiarFotos = tienePermiso(access, 'CLIENTS CREATE');

	const falloDeCarga = (mensaje) => {
		setIsLoading(false);
		setCargando(false);
		if (onFallo) onFallo();
		if (mensaje === 'Unauthorized') {
			setIsUnautorized(true);
			return;
		}
		setError(true);
		if (mensaje === ERROR_OTRA_SESION) {
			simpleMessage(mensaje, 'error');
		}
	};

	const cargar = async () => {
		setCargando(true);
		setError(false);
		setIsLoading(true);
		try {
			const result = await getClientByIdAsync(token, id);

			if (!result.statusResponse) {
				falloDeCarga(result.error);
				return;
			}

			const resultDepto = await getOnlyDepartmentListAsync(token);

			if (!resultDepto.statusResponse) {
				falloDeCarga(resultDepto.error);
				return;
			}

			setDepartmentList(resultDepto.data.result);

			const resultMun = await getMunicipalitiesByDeptopAsync(
				token,
				result.data.result.community.municipality.department.id
			);
			if (!resultMun.statusResponse) {
				falloDeCarga(resultMun.error);
				return;
			}
			setMunicipalitiesList(resultMun.data.result);

			const resultComm = await getCommunitiesByMunAsync(
				token,
				result.data.result.community.municipality.id
			);
			if (!resultComm.statusResponse) {
				falloDeCarga(resultComm.error);
				return;
			}

			setCommunitiesList(resultComm.data.result);

			const cliente = result.data.result;
			setNombre(cliente.fullName);
			setValores({
				firstName: cliente.firstName,
				secondName: cliente.secondName,
				lastName: cliente.lastName,
				secondLastName: cliente.secondLastName,
				phone: cliente.phone,
				identification: cliente.identification,
				sex: cliente.sex === 'MASCULINO' ? 0 : 1,
				civilStatus: getCivilStatus(cliente.civilStatus),
				department: cliente.community.municipality.department.id,
				municipality: cliente.community.municipality.id,
				community: cliente.community.id,
				address: cliente.address,
				actividadEconomica: cliente.actividadEconomica,
				company: cliente.company,
				companyAddress: cliente.companyAddress
			});
			setClientPhotos(cliente.clientPhotos);
			setErrores({});
			onDatos({
				fullName: cliente.fullName,
				identification: cliente.identification,
				phone: cliente.phone,
				address: cliente.address,
				ubicacion: [
					cliente.community.name,
					cliente.community.municipality.name,
					cliente.community.municipality.department.name
				]
					.filter(Boolean)
					.join(', ')
			});

			setIsLoading(false);
			setCargando(false);
		} catch (e) {
			// Sin red, `errorResponse` lanza una excepción: ni esqueleto eterno ni overlay encendido.
			setIsLoading(false);
			setCargando(false);
			if (onFallo) onFallo();
			setError('conexion');
		}
	};

	useEffect(() => {
		cargar();
	}, [localReload]);

	// Escribir o elegir en un campo limpia su error.
	const cambiar = (campo, valor) => {
		setValores((actual) => ({ ...actual, [campo]: valor }));
		if (errores[campo]) setErrores({ ...errores, [campo]: undefined });
	};

	const handleChangeDepartment = async (event) => {
		const valor = event.target.value;
		const peticion = ++peticionMunicipios.current;
		++peticionComunidades.current;
		setMunicipalitiesList([]);
		setCommunitiesList([]);
		setCargandoUbicacion({});
		cambiar('department', valor);

		if (valor !== '') {
			setCargandoUbicacion({ municipios: true });
			try {
				const result = await getMunicipalitiesByDeptopAsync(token, valor);
				if (peticion !== peticionMunicipios.current) return;
				setCargandoUbicacion({});
				if (!result.statusResponse) {
					setIsLoading(false);
					if (result.error === 'Unauthorized') {
						setIsUnautorized(true);
						return;
					}
					aviso.error(result.error || 'No se pudieron cargar los municipios');
					return;
				}
				setMunicipalitiesList(result.data.result);
			} catch (e) {
				if (peticion !== peticionMunicipios.current) return;
				setCargandoUbicacion({});
				aviso.error('No se pudieron cargar los municipios. Revisa tu conexión.');
			}
		} else {
			setMunicipalitiesList([]);
		}
	};

	const handleChangeMunicipality = async (event) => {
		const valor = event.target.value;
		const peticion = ++peticionComunidades.current;
		setCommunitiesList([]);
		setCargandoUbicacion({});
		cambiar('municipality', valor);

		if (valor !== '') {
			setCargandoUbicacion({ comunidades: true });
			try {
				const result = await getCommunitiesByMunAsync(token, valor);
				if (peticion !== peticionComunidades.current) return;
				setCargandoUbicacion({});
				if (!result.statusResponse) {
					setIsLoading(false);
					if (result.error === 'Unauthorized') {
						setIsUnautorized(true);
						return;
					}
					aviso.error(result.error || 'No se pudieron cargar las comunidades');
					return;
				}
				setCommunitiesList(result.data.result);
			} catch (e) {
				if (peticion !== peticionComunidades.current) return;
				setCargandoUbicacion({});
				aviso.error('No se pudieron cargar las comunidades. Revisa tu conexión.');
			}
		} else {
			setCommunitiesList([]);
		}
	};

	const saveChanges = async () => {
		if (enviando.current) {
			return;
		}
		// Mismas reglas de siempre, en el mismo orden; ahora se muestran todas juntas, cada una
		// junto a su campo, en vez de un aviso a la vez.
		const faltantes = validarCliente(valores);
		setErrores(faltantes);
		if (!isEmpty(faltantes)) {
			llevarAlPrimerError(formulario);
			return;
		}

		enviando.current = true;
		setGuardando(true);
		const { sex, civilStatus } = valores;
		const datos = {
			id,
			firstName: valores.firstName,
			secondName: valores.secondName,
			lastName: valores.lastName,
			secondLastName: valores.secondLastName,
			identification: valores.identification,
			phone: valores.phone,
			sex: sex === 0 ? 'MASCULINO' : 'FEMENINO',
			civilStatus:
				civilStatus === 0
					? sex === 0
						? 'SOLTERO'
						: 'SOLTERA'
					: civilStatus === 1
					? sex === 0
						? 'CASADO'
						: 'CASADA'
					: 'UNION LIBRE',
			idCommunity: valores.community,
			address: valores.address,
			company: valores.company,
			companyAddress: valores.companyAddress,
			actividadEconomica: valores.actividadEconomica
		};

		setIsLoading(true);
		let result;
		try {
			result = await updateClientAsync(token, datos);
		} catch (e) {
			enviando.current = false;
			setGuardando(false);
			setIsLoading(false);
			aviso.error('No se pudo conectar con el servidor. Inténtalo de nuevo.');
			return;
		}
		enviando.current = false;
		setGuardando(false);
		if (!result.statusResponse) {
			setIsLoading(false);

			if (result.error === 'Unauthorized') {
				setIsUnautorized(true);
				return;
			}

			aviso.error(result.error || 'No se pudo guardar el cliente');
			return;
		}

		setIsLoading(false);
		setIsEdit(false);
		// El encabezado y el resumen se actualizan con lo guardado (el servidor arma el nombre completo igual).
		const completo = nombreCompleto(valores);
		setNombre(completo);
		onDatos({
			fullName: completo,
			identification: valores.identification,
			phone: valores.phone,
			address: valores.address,
			ubicacion: [
				nombreDe(communitiesList, valores.community),
				nombreDe(MunicipalitiesList, valores.municipality),
				nombreDe(departmentList, valores.department)
			]
				.filter(Boolean)
				.join(', ')
		});
		aviso.exito('Cliente actualizado');
	};

	const handleOpen = (imageUrl) => {
		setSelectedImage(imageUrl);
		setOpenModal(true);
	};

	if (cargando) {
		return <CargandoFormularioCliente plana etiqueta="Cargando los datos del cliente…" />;
	}

	if (error) {
		return (
			<EstadoError
				titulo={
					error === 'conexion'
						? 'No se pudo conectar con el servidor'
						: 'No se pudieron cargar los datos del cliente'
				}
				onReintentar={cargar}
			/>
		);
	}

	return (
		<div>
			<Stack
				direction={{ xs: 'column', sm: 'row' }}
				alignItems={{ xs: 'flex-start', sm: 'center' }}
				justifyContent="space-between"
				spacing={1}
				sx={{ mb: 3 }}
			>
				<Typography variant="body2" sx={{ color: 'text.secondary' }}>
					{puedeEditar
						? isEdit
							? 'Cambia lo que haga falta y pulsa «Guardar cambios».'
							: 'Pulsa «Editar» para cambiar los datos del cliente.'
						: 'Estos son los datos del cliente.'}
				</Typography>

				{puedeEditar ? (
					<Button
						onClick={() => {
							setIsEdit(!isEdit);
						}}
						aria-pressed={isEdit}
						variant={isEdit ? 'contained' : 'outlined'}
						startIcon={<FontAwesomeIcon icon={faPenToSquare} />}
					>
						Editar
					</Button>
				) : null}
			</Stack>

			<Box ref={formulario}>
				<FormularioCliente
					plana
					prefijo={prefijo}
					valores={valores}
					errores={errores}
					onCambio={cambiar}
					onDepartamento={handleChangeDepartment}
					onMunicipio={handleChangeMunicipality}
					listas={{
						departamentos: departmentList,
						municipios: MunicipalitiesList,
						comunidades: communitiesList
					}}
					cargandoUbicacion={cargandoUbicacion}
					soloLectura={!isEdit}
				/>

				<SeccionFormulario
					plana
					titulo="Fotos del cliente"
					acciones={
						puedeEditar && puedeCambiarFotos ? (
							<Button
								onClick={() => {
									setEditPhotos(true);
								}}
								variant="outlined"
								startIcon={<FontAwesomeIcon icon={faCamera} />}
								disabled={!isEdit}
							>
								Cambiar fotos
							</Button>
						) : undefined
					}
				>
					{clientPhotos.length === 0 ? (
						<Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
							Este cliente no tiene fotos.
						</Typography>
					) : (
						<Grid container spacing={2} sx={{ mt: 0.5 }}>
							{clientPhotos.map((preview, index) => {
								return (
									<Grid item xs={12} sm={6} md={4} key={index}>
										<Box
											sx={{
												position: 'relative',
												p: 1,
												height: 200,
												display: 'flex',
												alignItems: 'center',
												justifyContent: 'center',
												border: 1,
												borderColor: 'divider',
												borderRadius: 2,
												bgcolor: 'background.default'
											}}
										>
											<img
												src={preview.imageFullPath}
												alt={`Foto ${index + 1} de ${nombre}`}
												style={{
													maxWidth: '100%',
													maxHeight: '100%',
													objectFit: 'contain'
												}}
											/>

											{/* Botón de zoom */}
											<IconButton
												aria-label={`Ampliar la foto ${index + 1}`}
												sx={{
													position: 'absolute',
													top: 8,
													right: 8,
													bgcolor: 'background.paper',
													border: 1,
													borderColor: 'divider',
													'&:hover': { bgcolor: 'background.paper' },
													width: 32,
													height: 32
												}}
												onClick={() => handleOpen(preview.imageFullPath)}
											>
												<ZoomInIcon sx={{ fontSize: 18 }} />
											</IconButton>
										</Box>
									</Grid>
								);
							})}
						</Grid>
					)}
				</SeccionFormulario>
			</Box>

			{puedeEditar ? (
				<Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
					<Button
						variant="contained"
						size="large"
						onClick={() => saveChanges()}
						disabled={!isEdit || guardando}
						startIcon={guardando ? <CircularProgress size={18} color="inherit" /> : null}
						sx={{ width: { xs: '100%', sm: 'auto' }, minWidth: 220 }}
					>
						{guardando ? 'Guardando…' : 'Guardar cambios'}
					</Button>
				</Box>
			) : null}

			<ClientPhoto
				id={id}
				abierto={editPhotos}
				setEditPhotos={setEditPhotos}
				onActualizado={() => setLocalReload(!localReload)}
			/>

			<PreviewModal
				openModal={openModal}
				selectedImage={selectedImage}
				setOpenModal={setOpenModal}
				setSelectedImage={setSelectedImage}
			/>
		</div>
	);
};

export default ClientDetails;
